'use client';
import React from 'react';
import type { GameRules } from '@/lib/gameEngine';

type Props = {
  rules: GameRules;
  onUpdateRules: (rules: GameRules) => void;
  onNext: () => void;
  onBack: () => void;
};

const RULE_CONFIGS = [
  {
    key: 'balancingPhase' as keyof GameRules,
    name: 'Balancing Phase',
    description:
      'After all pieces are placed, players take turns protecting one piece and removing an unprotected opponent piece. Recalculates formations.',
    recommended: true,
    officialRule: true,
  },
  {
    key: 'texasTieBreak' as keyof GameRules,
    name: 'Texas Tie Break',
    description:
      'In case of a score tie, the player with the most total formations wins. Prevents draws in competitive play.',
    recommended: true,
    officialRule: true,
  },
  {
    key: 'sweetwaterRule' as keyof GameRules,
    name: 'Sweetwater Rule',
    description:
      'Restricts access to Spot 9 — players cannot occupy the shared position unless it\'s a critical strategic move.',
    recommended: false,
    officialRule: true,
  },
  {
    key: 'chunnelRule' as keyof GameRules,
    name: 'Chunnel Rule',
    description:
      'Enables diagonal connections between the two grids through Spot 9, creating additional formation possibilities.',
    recommended: false,
    officialRule: true,
  },
];

export default function RulesStep({ rules, onUpdateRules, onNext, onBack }: Props) {
  const toggleRule = (key: keyof GameRules) => {
    onUpdateRules({ ...rules, [key]: !rules[key] });
  };

  const setPreset = (preset: 'standard' | 'competitive' | 'casual') => {
    if (preset === 'standard') {
      onUpdateRules({ balancingPhase: true, texasTieBreak: true, sweetwaterRule: false, chunnelRule: false });
    } else if (preset === 'competitive') {
      onUpdateRules({ balancingPhase: true, texasTieBreak: true, sweetwaterRule: true, chunnelRule: false });
    } else {
      onUpdateRules({ balancingPhase: false, texasTieBreak: false, sweetwaterRule: false, chunnelRule: false });
    }
  };

  return (
    <div className="animate-fade-in">
      <div className="text-center mb-8">
        <h2 className="text-hero-md text-foreground mb-3">Game Rules</h2>
        <p className="text-muted-foreground">
          Configure which official ViNCi rules to apply this session.
        </p>
      </div>

      {/* Presets */}
      <div className="mb-6">
        <label className="block text-xs font-600 text-muted-foreground uppercase tracking-wider mb-3">
          Quick Presets
        </label>
        <div className="flex gap-3">
          {[
            { id: 'standard', label: 'Standard', desc: 'Balancing + Tie Break' },
            { id: 'competitive', label: 'Competitive', desc: '+ Sweetwater' },
            { id: 'casual', label: 'Casual', desc: 'No special rules' },
          ].map((preset) => (
            <button
              key={`preset-${preset.id}`}
              onClick={() => setPreset(preset.id as 'standard' | 'competitive' | 'casual')}
              className="flex-1 py-3 px-3 rounded-xl border border-border bg-card hover:border-primary/40 hover:bg-secondary/50 active:scale-95 transition-all duration-150 text-center"
            >
              <div className="font-600 text-sm text-foreground">{preset.label}</div>
              <div className="text-xs text-muted-foreground mt-0.5">{preset.desc}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Individual rules */}
      <div className="flex flex-col gap-3">
        {RULE_CONFIGS.map((rule) => {
          const isEnabled = rules[rule.key];
          return (
            <div
              key={`rule-${rule.key}`}
              className={`rounded-2xl border p-5 transition-all duration-200 ${
                isEnabled ? 'border-primary/40 bg-primary/5' : 'border-border bg-card'
              }`}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1.5">
                    <span className="font-700 text-foreground text-sm">{rule.name}</span>
                    {rule.recommended && (
                      <span className="px-2 py-0.5 rounded-full text-xs font-600 bg-accent/20 text-accent border border-accent/30">
                        Recommended
                      </span>
                    )}
                    {rule.officialRule && (
                      <span className="px-2 py-0.5 rounded-full text-xs font-600 bg-primary/20 text-secondary-foreground border border-primary/30">
                        Official
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground leading-relaxed">{rule.description}</p>
                </div>
                <button
                  onClick={() => toggleRule(rule.key)}
                  className={`relative flex-shrink-0 w-12 h-6 rounded-full transition-all duration-300 ${
                    isEnabled ? 'bg-primary' : 'bg-muted'
                  }`}
                >
                  <span
                    className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-all duration-300 ${
                      isEnabled ? 'left-6' : 'left-0.5'
                    }`}
                  />
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Active rules summary */}
      <div className="mt-5 p-4 rounded-xl bg-secondary/50 border border-border">
        <div className="text-xs font-600 text-muted-foreground uppercase tracking-wider mb-2">
          Active Rules
        </div>
        <div className="flex flex-wrap gap-2">
          {RULE_CONFIGS.filter((r) => rules[r.key]).map((r) => (
            <span
              key={`active-rule-${r.key}`}
              className="px-2.5 py-1 rounded-lg text-xs font-600 bg-primary/20 text-secondary-foreground"
            >
              {r.name}
            </span>
          ))}
          {RULE_CONFIGS.every((r) => !rules[r.key]) && (
            <span className="text-xs text-muted-foreground">No special rules active — standard play</span>
          )}
        </div>
      </div>

      {/* Navigation */}
      <div className="flex gap-3 mt-8">
        <button
          onClick={onBack}
          className="flex-1 py-3.5 rounded-2xl border border-border text-foreground font-600 hover:bg-secondary/50 active:scale-95 transition-all duration-150"
        >
          Back
        </button>
        <button
          onClick={onNext}
          className="flex-grow py-3.5 rounded-2xl bg-primary text-primary-foreground font-700 hover:bg-primary/90 active:scale-95 transition-all duration-150 glow-primary"
        >
          Roll for Order →
        </button>
      </div>
    </div>
  );
}